import { useCallback } from "react";
import { useSelector } from "react-redux";
import { getStoreCurrencyPrefix, getStoreCurrencySuffix } from "@selectors/store";
import { useStoreContext } from "@components/store/provider";

import { formatPrice, convertPrice } from "@helpers/currency";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";

const useStoreCurrencyConverter = () => {
  const storeCurrencyPrefix = useSelector(getStoreCurrencyPrefix);
  const storeCurrencySuffix = useSelector(getStoreCurrencySuffix);
  const { storeDisplayCurrencyCode, storeCurrencyCode } = useStoreContext();
  const conversionRates = useConfig(getConversionRates);

  const convertPriceInGivenCurrency = useCallback(
    (price, baseCurrency, toCurrency, options = {}) => {
      if (!price) return price;

      if (baseCurrency === toCurrency || !baseCurrency) {
        return options.ceilRequired || options.ceilRequiredBeforeConversion
          ? Math.ceil(price.toFixed(4))
          : price;
      } else {
        return convertPrice(
          {
            from: baseCurrency,
            to: toCurrency,
            value: price,
            conversionRates,
          },
          options,
        );
      }
    },
    [conversionRates],
  );

  const formatPriceInGivenCurrency = useCallback(
    (price, baseCurrency, toCurrency, options = {}) => {
      if (!options.formatZero && !price) return price;
      let convertedPrice = convertPriceInGivenCurrency(price, baseCurrency, toCurrency, options);
      return formatPrice(convertedPrice, {
        prefix: storeCurrencyPrefix,
        suffix: storeCurrencySuffix,
        currencyCode: toCurrency,
        ...(options.currencyDisplay && { currencyDisplay: options.currencyDisplay }),
      });
    },
    [storeCurrencyPrefix, storeCurrencySuffix, convertPriceInGivenCurrency],
  );

  const formatPriceInStoreCurrency = useCallback(
    (price, currencyCode, options = {}) => {
      return formatPriceInGivenCurrency(
        price,
        currencyCode || storeCurrencyCode,
        storeCurrencyCode,
        options,
      );
    },
    [storeCurrencyCode, formatPriceInGivenCurrency],
  );

  const convertPriceInStoreCurrency = useCallback(
    (price, currencyCode, options = {}) => {
      return convertPriceInGivenCurrency(
        price,
        currencyCode || storeCurrencyCode,
        storeCurrencyCode,
        options,
      );
    },
    [storeCurrencyCode, convertPriceInGivenCurrency],
  );

  const formatPriceInDisplayStoreCurrency = useCallback(
    (price, currencyCode, options = {}) => {
      return formatPriceInGivenCurrency(
        price,
        currencyCode || storeCurrencyCode,
        storeDisplayCurrencyCode,
        options,
      );
    },
    [storeCurrencyCode, formatPriceInGivenCurrency, storeDisplayCurrencyCode],
  );

  const convertPriceInDisplayStoreCurrency = useCallback(
    (price, currencyCode, options = {}) => {
      return convertPriceInGivenCurrency(
        price,
        currencyCode || storeCurrencyCode,
        storeDisplayCurrencyCode,
        options,
      );
    },
    [storeCurrencyCode, convertPriceInGivenCurrency, storeDisplayCurrencyCode],
  );

  return {
    convertPriceInDisplayStoreCurrency,
    convertPriceInStoreCurrency,
    formatPriceInDisplayStoreCurrency,
    formatPriceInStoreCurrency,
    formatPriceInGivenCurrency,
  };
};

export default useStoreCurrencyConverter;
